<template>
  <div class="home-modern">
    <b-alert variant="danger" v-model="localShowAuthError" dismissible>
      {{ local_auth_error_message }}
    </b-alert>

    <!-- Bacteriological sampling announcement -->
    <b-card class="mb-3 announcement-card" border-variant="warning" bg-variant="light">
      <b-row align-v="center" class="clickable" @click="showBactFormModal = true">
        <b-col cols="auto">
          <b-icon icon="exclamation-triangle-fill" font-scale="2" variant="warning"></b-icon>
        </b-col>
        <b-col>
          <h6 class="mb-0 text-warning">Important: Bacteriological Sampling</h6>
          <small class="text-muted">Tap for revised Form 425 and Bact bar code label instructions</small>
        </b-col>
        <b-col cols="auto">
          <b-icon icon="chevron-right" variant="muted"></b-icon>
        </b-col>
      </b-row>
    </b-card>

    <!-- Monitoring Waivers Announcement Banner - can be removed after Dec 31, 2025 -->
    <b-card v-if="showWaiverAnnouncement" class="mb-3 announcement-card" border-variant="info" bg-variant="light">
      <b-row align-v="center" @click="handleWaiverClick" class="clickable">
        <b-col cols="auto">
          <b-icon icon="megaphone-fill" font-scale="2" variant="info"></b-icon>
        </b-col>
        <b-col>
          <h6 class="mb-0 text-info">Monitoring Waivers Available</h6>
          <small class="text-muted">Apply before 2026 sampling begins</small>
        </b-col>
        <b-col cols="auto">
          <b-icon icon="chevron-right" variant="muted"></b-icon>
        </b-col>
      </b-row>
    </b-card>

    <!-- Needs Attention Section -->
    <div v-if="signed_in && (sigdef_status.needs_attention || message_count > 0 || lsli_notice_due || vocWaiverActionNeeded || boardMembersCorrections || rtcrCorrections || rtcrPlanDue)" class="mb-4">
      <b-card class="attention-card border-0 shadow-sm">
        <template #header>
          <div class="d-flex align-items-center">
            <b-icon icon="bell-fill" class="text-danger mr-2"></b-icon>
            <span class="font-weight-bold">Needs Your Attention</span>
          </div>
        </template>
        <b-list-group flush>
          <b-list-group-item v-if="sigdef_status.needs_attention" to="sigdef-responses" class="action-item d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div class="icon-circle bg-danger mr-3">
                <b-icon icon="clipboard-check" variant="light"></b-icon>
              </div>
              <div>
                <div class="font-weight-medium">Significant Deficiencies</div>
                <small class="text-muted">Action required</small>
              </div>
            </div>
            <b-icon icon="chevron-right" variant="muted"></b-icon>
          </b-list-group-item>
          <b-list-group-item v-if="message_count > 0" to="messages" class="action-item d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div class="icon-circle bg-warning mr-3">
                <b-icon icon="inbox-fill" variant="light"></b-icon>
              </div>
              <div>
                <div class="font-weight-medium">Messages</div>
                <small class="text-muted">{{ message_count }} unread</small>
              </div>
            </div>
            <b-icon icon="chevron-right" variant="muted"></b-icon>
          </b-list-group-item>
          <b-list-group-item v-if="lsli_notice_due" to="lead-inventory" class="action-item d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div class="icon-circle bg-danger mr-3">
                <b-icon icon="droplet-half" variant="light"></b-icon>
              </div>
              <div>
                <div class="font-weight-medium">Lead Service Line Notice</div>
                <small class="text-muted">{{ lsli_notice_year }} confirmation due before July 1</small>
              </div>
            </div>
            <b-icon icon="chevron-right" variant="muted"></b-icon>
          </b-list-group-item>
          <b-list-group-item v-if="boardMembersCorrections" :to="{ path: '/board-members', query: { tab: 'status' } }" class="action-item d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div class="icon-circle bg-warning mr-3">
                <b-icon icon="people" variant="light"></b-icon>
              </div>
              <div>
                <div class="font-weight-medium">Board Members</div>
                <small class="text-muted">Corrections required</small>
              </div>
            </div>
            <b-icon icon="chevron-right" variant="muted"></b-icon>
          </b-list-group-item>
          <b-list-group-item v-if="rtcrCorrections || rtcrPlanDue" to="rtcr" class="action-item d-flex justify-content-between align-items-center rtcr-attention">
            <div class="d-flex align-items-center">
              <div class="icon-circle bg-warning mr-3">
                <b-icon icon="map" variant="light"></b-icon>
              </div>
              <div>
                <div class="font-weight-medium">RTCR Sample Site Plan</div>
                <small class="text-muted">{{ rtcrCorrections ? 'Corrections required' : rtcrPlanDue }}</small>
              </div>
            </div>
            <b-icon icon="chevron-right" variant="muted"></b-icon>
          </b-list-group-item>
          <b-list-group-item v-if="vocWaiverActionNeeded" to="monitoring-waivers" class="action-item d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div class="icon-circle bg-info mr-3">
                <b-icon icon="clipboard-check" variant="light"></b-icon>
              </div>
              <div>
                <div class="font-weight-medium">VOC Monitoring Waiver</div>
                <small class="text-muted">Your system may qualify — apply to reduce monitoring</small>
              </div>
            </div>
            <b-icon icon="chevron-right" variant="muted"></b-icon>
          </b-list-group-item>
        </b-list-group>
      </b-card>
    </div>

    <!-- System Vitals -->
    <SystemVitals />

    <!-- Quick Actions (populated based on user's actual usage) -->
    <div v-if="topQuickActions.length > 0" class="mb-4">
      <h6 class="text-muted text-uppercase mb-3 small font-weight-bold">Quick Actions</h6>
      <b-row>
        <b-col v-for="action in topQuickActions" :key="action.route" cols="6" md="3" class="mb-3">
          <div class="quick-action-card text-center p-3 h-100 position-relative">
            <button
              class="quick-action-remove"
              @click.stop="removeQuickAction(action.route)"
              title="Remove from Quick Actions"
            >
              <b-icon icon="x" font-scale="0.9"></b-icon>
            </button>
            <div class="clickable" @click="handleQuickAction(action.route)">
              <div class="icon-circle-lg mx-auto mb-2" :class="'bg-' + action.color">
                <b-icon :icon="action.icon" variant="light" font-scale="1.5"></b-icon>
              </div>
              <div class="small font-weight-medium">{{ action.label }}</div>
              <b-badge v-if="getActionBadge(action.route)" :variant="getActionBadge(action.route).variant" class="mt-1">
                {{ getActionBadge(action.route).text }}
              </b-badge>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Category Cards -->
    <b-row>
      <!-- Reports & Submissions -->
      <b-col cols="12" lg="6" class="mb-4">
        <b-card class="category-card h-100 border-0 shadow-sm">
          <template #header>
            <div class="d-flex align-items-center">
              <div class="category-icon bg-reports mr-2">
                <b-icon icon="file-earmark-text" variant="light"></b-icon>
              </div>
              <span class="font-weight-bold">Reports & Submissions</span>
            </div>
          </template>
          <b-list-group flush>
            <b-list-group-item
              :to="signed_in ? 'pws-report-status' : null"
              @click="signed_in ? trackFeatureUsage('pws-report-status') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>Public Water System Report</span>
              <div class="d-flex align-items-center">
                <b-badge v-if="annualReportDeadlineToday" variant="danger" class="mr-2">Due</b-badge>
                <b-icon icon="chevron-right" variant="muted"></b-icon>
              </div>
            </b-list-group-item>
            <b-list-group-item to="ccr" @click="trackFeatureUsage('ccr')" class="action-item d-flex justify-content-between align-items-center">
              <span>Consumer Confidence Report</span>
              <b-icon icon="chevron-right" variant="muted"></b-icon>
            </b-list-group-item>
            <b-list-group-item
              :to="signed_in ? 'surveys' : null"
              @click="signed_in ? trackFeatureUsage('surveys') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>Surveys</span>
              <b-icon icon="chevron-right" variant="muted"></b-icon>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>

      <!-- Water Quality Testing -->
      <b-col cols="12" lg="6" class="mb-4">
        <b-card class="category-card h-100 border-0 shadow-sm">
          <template #header>
            <div class="d-flex align-items-center">
              <div class="category-icon bg-water mr-2">
                <b-icon icon="droplet" variant="light"></b-icon>
              </div>
              <span class="font-weight-bold">Water Quality Testing</span>
            </div>
          </template>
          <b-list-group flush>
            <b-list-group-item
              :to="signed_in ? 'sample-results' : null"
              @click="signed_in ? trackFeatureUsage('sample-results') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>Sample Results</span>
              <b-icon icon="chevron-right" variant="muted"></b-icon>
            </b-list-group-item>
            <b-list-group-item
              :to="signed_in ? 'sample-schedules' : null"
              @click="signed_in ? trackFeatureUsage('sample-schedules') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>Sample Schedules</span>
              <b-icon icon="chevron-right" variant="muted"></b-icon>
            </b-list-group-item>
            <b-list-group-item
              :to="signed_in ? 'rtcr' : null"
              @click="signed_in ? trackFeatureUsage('rtcr') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>RTCR Sample Site Plans</span>
              <div class="d-flex align-items-center">
                <b-badge v-if="rtcrCorrections" variant="warning" class="mr-2 rtcr-badge">Corrections Required</b-badge>
                <b-icon icon="chevron-right" variant="muted"></b-icon>
              </div>
            </b-list-group-item>
            <b-list-group-item
              :to="signed_in ? 'monitoring-waivers' : null"
              @click="signed_in ? trackFeatureUsage('monitoring-waivers') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>Monitoring Waivers</span>
              <div class="d-flex align-items-center">
                <b-badge v-if="waiverDeadlineToday" variant="danger" class="mr-2">Due</b-badge>
                <b-badge v-if="vocWaiverActionNeeded" variant="info" class="mr-2">!</b-badge>
                <b-icon icon="chevron-right" variant="muted"></b-icon>
              </div>
            </b-list-group-item>
            <b-list-group-item
              v-if="signed_in && fourlogCovered"
              to="fourlog"
              @click="trackFeatureUsage('fourlog')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>4-Log Residual Log</span>
              <b-icon icon="chevron-right" variant="muted"></b-icon>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>

      <!-- Lead Testing -->
      <b-col cols="12" lg="6" class="mb-4">
        <b-card class="category-card h-100 border-0 shadow-sm">
          <template #header>
            <div class="d-flex align-items-center">
              <div class="category-icon bg-lead mr-2">
                <b-icon icon="card-checklist" variant="light"></b-icon>
              </div>
              <span class="font-weight-bold">Lead Testing & Inventory</span>
            </div>
          </template>
          <b-list-group flush>
            <b-list-group-item
              :to="signed_in ? 'lead-results' : null"
              @click="signed_in ? trackFeatureUsage('lead-results') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>Lead and Copper Sampling</span>
              <b-icon icon="chevron-right" variant="muted"></b-icon>
            </b-list-group-item>
            <b-list-group-item
              :to="signed_in ? 'lead-inventory' : null"
              @click="signed_in ? trackFeatureUsage('lead-inventory') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>Lead Service Line Inventory</span>
              <b-icon icon="chevron-right" variant="muted"></b-icon>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>

      <!-- Compliance & Notices -->
      <b-col cols="12" lg="6" class="mb-4">
        <b-card class="category-card h-100 border-0 shadow-sm">
          <template #header>
            <div class="d-flex align-items-center">
              <div class="category-icon bg-compliance mr-2">
                <b-icon icon="shield-check" variant="light"></b-icon>
              </div>
              <span class="font-weight-bold">Compliance & Notices</span>
            </div>
          </template>
          <b-list-group flush>
            <b-list-group-item
              :to="signed_in && sigdef_status.available ? 'sigdef-responses' : null"
              :disabled="!signed_in || !sigdef_status.available"
              @click="signed_in && sigdef_status.available && trackFeatureUsage('sigdef-responses')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>Significant Deficiencies</span>
              <div class="d-flex align-items-center">
                <b-badge v-if="sigdef_status.needs_attention" variant="danger" class="mr-2">!</b-badge>
                <b-icon icon="chevron-right" variant="muted"></b-icon>
              </div>
            </b-list-group-item>
            <b-list-group-item
              :to="signed_in ? 'emergency' : null"
              @click="signed_in ? trackFeatureUsage('emergency') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>Emergency Notice</span>
              <b-icon icon="chevron-right" variant="muted"></b-icon>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>

      <!-- System Management -->
      <b-col cols="12" lg="6" class="mb-4">
        <b-card class="category-card h-100 border-0 shadow-sm">
          <template #header>
            <div class="d-flex align-items-center">
              <div class="category-icon bg-management mr-2">
                <b-icon icon="gear" variant="light"></b-icon>
              </div>
              <span class="font-weight-bold">System Management</span>
            </div>
          </template>
          <b-list-group flush>
            <b-list-group-item
              :to="signed_in ? 'board-members' : null"
              @click="signed_in ? trackFeatureUsage('board-members') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>Board Members</span>
              <div class="d-flex align-items-center">
                <b-badge v-if="boardMembersDeadlineToday" variant="danger" class="mr-2">Due</b-badge>
                <b-badge v-else-if="boardMembersCorrections" variant="warning" class="mr-2">Corrections Required</b-badge>
                <b-icon icon="chevron-right" variant="muted"></b-icon>
              </div>
            </b-list-group-item>
            <b-list-group-item
              :to="signed_in ? 'pws-files' : null"
              @click="signed_in ? trackFeatureUsage('pws-files') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>File Cabinet</span>
              <b-icon icon="chevron-right" variant="muted"></b-icon>
            </b-list-group-item>
            <b-list-group-item
              :to="signed_in ? 'messages' : null"
              @click="signed_in ? trackFeatureUsage('messages') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>Messages</span>
              <div class="d-flex align-items-center">
                <b-badge v-if="message_count > 0" variant="warning" class="mr-2">{{ message_count }}</b-badge>
                <b-icon icon="chevron-right" variant="muted"></b-icon>
              </div>
            </b-list-group-item>
            <b-list-group-item
              :to="signed_in ? 'email-test' : null"
              @click="signed_in ? trackFeatureUsage('email-test') : $bvModal.show('sign-in-modal')"
              class="action-item d-flex justify-content-between align-items-center"
            >
              <span>E-Mail Tester</span>
              <b-icon icon="chevron-right" variant="muted"></b-icon>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>

      <!-- External Links -->
      <b-col cols="12" lg="6" class="mb-4">
        <b-card class="category-card h-100 border-0 shadow-sm">
          <template #header>
            <div class="d-flex align-items-center">
              <div class="category-icon bg-external mr-2">
                <b-icon icon="box-arrow-up-right" variant="light"></b-icon>
              </div>
              <span class="font-weight-bold">External Applications</span>
            </div>
          </template>
          <b-list-group flush>
            <b-list-group-item href="https://apps.msdh.ms.gov/DWW/" target="_blank" class="action-item d-flex justify-content-between align-items-center">
              <span>Drinking Water Watch</span>
              <b-icon icon="box-arrow-up-right" variant="muted"></b-icon>
            </b-list-group-item>
            <b-list-group-item href="https://samples.mswater.us" target="_blank" class="action-item d-flex justify-content-between align-items-center">
              <span>TCR Sample Notification App</span>
              <b-icon icon="box-arrow-up-right" variant="muted"></b-icon>
            </b-list-group-item>
            <b-list-group-item href="https://www.msdhwo.webapps.ms.gov/home.aspx" target="_blank" class="action-item d-flex justify-content-between align-items-center">
              <span>LARS Login</span>
              <b-icon icon="box-arrow-up-right" variant="muted"></b-icon>
            </b-list-group-item>
            <b-list-group-item href="https://www.msdhwo.webapps.ms.gov/PublicView/PublicIndSearch.aspx" target="_blank" class="action-item d-flex justify-content-between align-items-center">
              <span>Find a Certified Operator</span>
              <b-icon icon="box-arrow-up-right" variant="muted"></b-icon>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>
    </b-row>

    <div class="mt-3 text-center">
      <b-link to="help">Need Help?</b-link>
    </div>

    <b-modal id="sign-in-modal" title="Sign In Required" @ok="$emit('nav-to-signin')">
      <p>You need to sign in before accessing this application.</p>
    </b-modal>

    <!-- Bacteriological sampling modal -->
    <b-modal v-model="showBactFormModal" title="Important messages regarding bacteriological sampling" size="lg" ok-only ok-title="Close">
      <p>
        The <strong>Form 425</strong> was revised. Copies should be available throughout county health departments.
      </p>
      <p>
        For <strong>Bact bar code labels</strong>, click on instructions below.
      </p>
      <p class="text-center">
        <b-button variant="primary" href="/static/docs/Bact_bar_code_labels.pdf" target="_blank">
          <b-icon icon="download"></b-icon> Download (PDF)
        </b-button>
      </p>
      <p class="text-muted">May 2026</p>
    </b-modal>
  </div>
</template>

<script>
import SystemVitals from "@/components/SystemVitals.vue"

// Feature metadata for Quick Actions
const FEATURE_META = {
  'sample-results': { label: 'Sample Results', icon: 'droplet', color: 'primary' },
  'pws-report-status': { label: 'PWS Report', icon: 'card-list', color: 'success' },
  'ccr': { label: 'CCR', icon: 'newspaper', color: 'info' },
  'messages': { label: 'Messages', icon: 'inbox-fill', color: 'secondary' },
  'sample-schedules': { label: 'Schedules', icon: 'calendar3', color: 'primary' },
  'rtcr': { label: 'RTCR Plans', icon: 'map', color: 'primary' },
  'monitoring-waivers': { label: 'Waivers', icon: 'clipboard-check', color: 'primary' },
  'fourlog': { label: '4-Log Log', icon: 'shield-check', color: 'primary' },
  'lead-results': { label: 'Lead & Copper', icon: 'droplet', color: 'danger' },
  'lead-inventory': { label: 'LSLI', icon: 'card-checklist', color: 'danger' },
  'sigdef-responses': { label: 'Sig Defs', icon: 'shield-check', color: 'warning' },
  'emergency': { label: 'Emergency', icon: 'exclamation-triangle', color: 'warning' },
  'board-members': { label: 'Board', icon: 'people', color: 'secondary' },
  'pws-files': { label: 'Files', icon: 'archive', color: 'secondary' },
  'surveys': { label: 'Surveys', icon: 'journal-check', color: 'success' },
  'email-test': { label: 'Email Test', icon: 'envelope', color: 'secondary' }
}

const USAGE_STORAGE_KEY = 'pws_portal_feature_usage'

// Usage counts are keyed by route path. When a feature's path changes, map the
// old key here so users keep their Quick Actions tile (REQ-41).
const RENAMED_FEATURES = {
  'annual-report-status': 'pws-report-status'
}

export default {
  name: "HomeModern",
  components: {
    SystemVitals
  },
  props: {
    signed_in: { type: Boolean, default: false },
    pws_id: { type: String, default: '' },
    role: { type: String, default: '' },
    message_count: { type: Number, default: 0 },
    sigdef_status: {
      type: Object,
      default: () => ({
        available: false,
        unresolved_count: 0,
        pending_response_count: 0,
        needs_attention: false,
      })
    },
    lsli_notice_due: { type: Boolean, default: false },
    lsli_notice_year: { type: [Number, String], default: null },
    vocWaiverActionNeeded: { type: Boolean, default: false },
    showWaiverAnnouncement: { type: Boolean, default: false },
    waiverDeadlineToday: { type: Boolean, default: false },
    annualReportDeadlineToday: { type: Boolean, default: false },
    boardMembersDeadlineToday: { type: Boolean, default: false },
    boardMembersCorrections: { type: Boolean, default: false },
    rtcrCorrections: { type: Boolean, default: false },
    rtcrPlanDue: { type: String, default: '' },
    fourlogCovered: { type: Boolean, default: false },
    showSigninAlert: { type: Boolean, default: false },
    showAuthError: { type: Boolean, default: false },
    authErrorMessage: { type: String, default: '' }
  },
  data() {
    return {
      showBactFormModal: false,
      localShowAuthError: this.showAuthError,
      local_auth_error_message: this.authErrorMessage,
      featureUsage: {}
    }
  },
  computed: {
    topQuickActions() {
      // Get top 4 most-used features (exclude items with 0 count)
      const entries = Object.entries(this.featureUsage)
        .filter(([route, count]) => FEATURE_META[route] && count > 0)
        .sort((a, b) => b[1] - a[1]) // Sort by usage count descending
        .slice(0, 4)

      return entries.map(([route]) => ({
        route,
        ...FEATURE_META[route]
      }))
    }
  },
  watch: {
    showAuthError(val) {
      this.localShowAuthError = val
    },
    authErrorMessage(val) {
      this.local_auth_error_message = val
    }
  },
  created() {
    this.loadFeatureUsage()
  },
  mounted() {
    if (this.showSigninAlert) {
      this.$bvToast.toast(`Signed in as ${this.role} for PWS ${this.pws_id}`, {
        title: 'Welcome',
        variant: 'success',
        solid: true,
        autoHideDelay: 4000
      })
    }
  },
  methods: {
    loadFeatureUsage() {
      try {
        const stored = localStorage.getItem(USAGE_STORAGE_KEY)
        if (stored) {
          const usage = JSON.parse(stored)
          let migrated = false
          Object.entries(RENAMED_FEATURES).forEach(([from, to]) => {
            if (from in usage) {
              usage[to] = (usage[to] || 0) + usage[from]
              delete usage[from]
              migrated = true
            }
          })
          this.featureUsage = usage
          if (migrated) {
            localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(usage))
          }
        }
      } catch (e) {
        console.error('Error loading feature usage:', e)
        this.featureUsage = {}
      }
    },
    trackFeatureUsage(route) {
      if (!FEATURE_META[route]) return // Only track known features

      const current = this.featureUsage[route] || 0
      this.$set(this.featureUsage, route, current + 1)

      try {
        localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(this.featureUsage))
      } catch (e) {
        console.error('Error saving feature usage:', e)
      }
    },
    getActionBadge(route) {
      if (route === 'messages' && this.message_count > 0) {
        return { variant: 'warning', text: this.message_count }
      }
      if (route === 'pws-report-status' && this.annualReportDeadlineToday) {
        return { variant: 'danger', text: 'Due' }
      }
      if (route === 'monitoring-waivers' && this.waiverDeadlineToday) {
        return { variant: 'danger', text: 'Due' }
      }
      if (route === 'board-members' && this.boardMembersDeadlineToday) {
        return { variant: 'danger', text: 'Due' }
      }
      if (route === 'board-members' && this.boardMembersCorrections) {
        return { variant: 'warning', text: '!' }
      }
      if (route === 'rtcr' && this.rtcrCorrections) {
        return { variant: 'warning', text: '!' }
      }
      if (route === 'sigdef-responses' && this.sigdef_status.needs_attention) {
        return { variant: 'danger', text: '!' }
      }
      return null
    },
    handleQuickAction(route) {
      if (!this.signed_in && route !== 'ccr') {
        this.$bvModal.show('sign-in-modal')
      } else {
        this.trackFeatureUsage(route)
        this.$router.push(route)
      }
    },
    handleWaiverClick() {
      if (!this.signed_in) {
        this.$bvModal.show('sign-in-modal')
      } else {
        this.trackFeatureUsage('monitoring-waivers')
        this.$router.push('monitoring-waivers')
      }
    },
    handleFeatureClick(route) {
      // Called when clicking items in category cards
      if (!this.signed_in && route !== 'ccr') {
        this.$bvModal.show('sign-in-modal')
        return false
      }
      this.trackFeatureUsage(route)
      return true // Allow navigation
    },
    removeQuickAction(route) {
      // Remove from quick actions by setting count to 0
      this.$set(this.featureUsage, route, 0)
      try {
        localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(this.featureUsage))
      } catch (e) {
        console.error('Error saving feature usage:', e)
      }
    }
  }
}
</script>

<style scoped>
/* Category icon colors */
.bg-reports { background-color: #28a745; }
.bg-water { background-color: #007bff; }
.bg-lead { background-color: #dc3545; }
.bg-compliance { background-color: #fd7e14; }
.bg-management { background-color: #6c757d; }
.bg-external { background-color: #17a2b8; }

/* Icon circles */
.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-circle-lg {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Category icon in card header */
.category-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Quick action cards */
.quick-action-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
}

.quick-action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

/* Category cards */
.category-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.category-card .card-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0.75rem 1rem;
}

/* Action items in list */
.action-item {
  min-height: 52px;
  padding: 0.75rem 1rem !important;
  border-left: none !important;
  border-right: none !important;
  transition: background-color 0.15s ease;
}

.action-item:hover {
  background-color: #f8f9fa;
}

.action-item:first-child {
  border-top: none !important;
}

/* Font weight helper */
.font-weight-medium {
  font-weight: 500;
}

/* Attention card */
.attention-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.attention-card .card-header {
  background: #fff5f5;
  border-bottom: 1px solid #fee2e2;
  padding: 0.75rem 1rem;
}

/* Announcement card styling */
.announcement-card {
  border-width: 2px !important;
  border-radius: 12px !important;
}

/* Clickable items */
.clickable {
  cursor: pointer;
}

/* Disabled state */
.list-group-item.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Quick action remove button */
.quick-action-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  color: #6c757d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}

.quick-action-card:hover .quick-action-remove {
  opacity: 1;
}

.quick-action-remove:hover {
  background: rgba(0, 0, 0, 0.2);
  color: #dc3545;
}
</style>
